import { useMemo } from 'react';

import { useStores } from '../../../stores/useStores';
import type { WidgetEntry } from '../types';

// Tie-breaker inside the Jackpot priority bucket. Only one jackpot widget exists per lobby (the
// payload is per coin type), so this only matters against widgets sharing the bucket.
const JACKPOT_WIDGET_WEIGHT = 200;

/**
 * Lobby rail entry for the Crown Jackpot (FE_DESIGN_SPEC §7.4 variants B and C — the rail decides
 * wide vs small, so JackpotWidget renders the promo card or the round nav icon accordingly).
 *
 * Eligibility is the shared JackpotStore poll: `canShowWidget` is false on `canSee: false`, on any
 * error, and while the first fetch is in flight, so the rail simply carries one fewer widget. No
 * fetch of its own — the category row, the promo card and the nav icon all ride one request.
 *
 * A maintenance window is the exception: it carries a slot with no data behind it at all (design
 * `10929:24559`), because telling a player the jackpot is briefly down beats the row vanishing.
 */
export function useJackpotWidgets() {
  const { jackpotStore } = useStores();
  const { canShowWidget, canShowMaintenanceWidget } = jackpotStore;

  return useMemo((): WidgetEntry<'jackpot'>[] => {
    /*
     * Two ways to earn a rail slot: the live widget, or the maintenance plate. The plate is NOT gated on
     * `canShowWidget` — in a maintenance window the flag is off, so nothing is fetched and there is no
     * amount to gate on. See JackpotStore.canShowMaintenanceWidget.
     */
    if (!canShowWidget && !canShowMaintenanceWidget) {
      return [];
    }

    return [
      {
        data: { key: 'jackpot', dataTestId: 'jackpot-widget', weight: JACKPOT_WIDGET_WEIGHT },
        type: 'jackpot',
        id: 'jackpot',
      },
    ];
  }, [canShowWidget, canShowMaintenanceWidget]);
}
